import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const THEMES = {
    light: { label: 'Light', icon: '☀️' },
    dark: { label: 'Dark', icon: '🌙' },
    midnight: { label: 'Midnight', icon: '🌌' },
    system: { label: 'System', icon: '💻' },
};

function getSystemTheme() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
}

function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        const saved = localStorage.getItem('stockify-theme');
        return saved && THEMES[saved] ? saved : 'light';
    });

    const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

    const applyTheme = useCallback((themeToApply) => {
        const root = document.documentElement;
        // Remove all theme classes
        root.removeAttribute('data-theme');
        root.classList.remove('dark', 'midnight');

        if (themeToApply === 'dark') {
            root.setAttribute('data-theme', 'dark');
            root.classList.add('dark');
        } else if (themeToApply === 'midnight') {
            root.setAttribute('data-theme', 'midnight');
            root.classList.add('midnight');
        } else {
            root.setAttribute('data-theme', 'light');
        }
    }, []);

    useEffect(() => {
        applyTheme(resolvedTheme);
    }, [resolvedTheme, applyTheme]);

    // Listen for system theme changes when in "system" mode
    useEffect(() => {
        if (theme !== 'system') return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => applyTheme(getSystemTheme());
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [theme, applyTheme]);

    const setTheme = (newTheme) => {
        setThemeState(newTheme);
        localStorage.setItem('stockify-theme', newTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, THEMES }}>
            {children}
        </ThemeContext.Provider>
    );
}

function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme must be used within a ThemeProvider');
    return context;
}

export { ThemeProvider, useTheme, THEMES };
